/**
 * Iframe + window.print() delivery for the review company export render page
 * (pages/portfolio/review/_requestId/export/_ventureId.vue): notify the parent
 * when the card is ready, print, then report completion.
 */
export const CARD_EXPORT_READY_MESSAGE_TYPE = 'card-export-ready'
export const CARD_EXPORT_FINISHED_MESSAGE_TYPE = 'card-export-finished'
export const CARD_EXPORT_IFRAME_AUTH_REQUIRED_TYPE =
  'card-export-iframe-auth-required'

export const CARD_EXPORT_IFRAME_SELECTOR = 'iframe[data-ix-card-export-iframe]'

export type CardExportStatus = 'success' | 'error'

type CardExportReadyMessage = {
  type: typeof CARD_EXPORT_READY_MESSAGE_TYPE
  exportId: string
  documentTitle?: string
}

type CardExportFinishedMessage = {
  type: typeof CARD_EXPORT_FINISHED_MESSAGE_TYPE
  exportId: string
  status: CardExportStatus
}

function postExportMessage(message: CardExportReadyMessage | CardExportFinishedMessage) {
  const targetOrigin = window.location.origin
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(message, targetOrigin)
    return
  }
  if (window.opener) {
    window.opener.postMessage(message, targetOrigin)
  }
}

export function notifyCardExportReady(exportId: string, documentTitle?: string) {
  postExportMessage({
    type: CARD_EXPORT_READY_MESSAGE_TYPE,
    exportId,
    ...(documentTitle ? { documentTitle } : {}),
  })
}

export function notifyCardExportComplete(options: {
  exportId: string
  status: CardExportStatus
}) {
  postExportMessage({
    type: CARD_EXPORT_FINISHED_MESSAGE_TYPE,
    exportId: options.exportId,
    status: options.status,
  })
}

/** Remove hidden export iframes (e.g. after navigation or a failed Safari session). */
export function removeStaleCardExportIframes() {
  document.querySelectorAll(CARD_EXPORT_IFRAME_SELECTOR).forEach((node) => {
    node.remove()
  })
}

const EMBEDDED_PRINT_FALLBACK_MS = 120000
const STANDALONE_PRINT_FALLBACK_MS = 4000
/** Delay after card-export-ready before window.print() (slow layout / scale passes). */
const EMBEDDED_PRINT_SETTLE_MS = 300
const STANDALONE_PRINT_SETTLE_MS = 800

export type CardExportDeliveryOptions = {
  /** Suggested PDF filename (browser uses document.title in the print dialog). */
  documentTitle?: string
}

type PrintTitleScope = {
  win: Window
  previousTitle: string
}

function collectPrintTitleWindows(): Window[] {
  const windows: Window[] = [window]
  try {
    if (window.parent && window.parent !== window) {
      windows.push(window.parent)
    }
    if (window.top && window.top !== window) {
      windows.push(window.top)
    }
  } catch {
    // Ignore cross-origin access errors.
  }
  return [...new Set(windows)]
}

function capturePrintTitleScopes(): PrintTitleScope[] {
  const scopes: PrintTitleScope[] = []
  for (const win of collectPrintTitleWindows()) {
    try {
      scopes.push({
        win,
        previousTitle: win.document.title,
      })
    } catch {
      // Ignore cross-origin access errors.
    }
  }
  return scopes
}

function writePrintTitle(win: Window, title: string) {
  win.document.title = title
  const titleEl = win.document.querySelector('title')
  if (titleEl) {
    titleEl.textContent = title
  }
}

function applyCardExportDocumentTitle(documentTitle?: string) {
  if (!documentTitle) {
    return
  }
  for (const win of collectPrintTitleWindows()) {
    try {
      writePrintTitle(win, documentTitle)
    } catch {
      // Ignore cross-origin access errors.
    }
  }
}

function restorePrintTitleScopes(scopes: PrintTitleScope[]) {
  for (const { win, previousTitle } of scopes) {
    try {
      writePrintTitle(win, previousTitle)
    } catch {
      // Ignore cross-origin access errors.
    }
  }
}

function bindCardExportPrintTitle(
  documentTitle: string | undefined,
  onAfterPrint: () => void,
) {
  if (!documentTitle) {
    return () => {}
  }

  const scopes = capturePrintTitleScopes().filter((scope) => scope.win === window)
  const applyTitle = () => applyCardExportDocumentTitle(documentTitle)
  const restoreTitle = () => {
    restorePrintTitleScopes(scopes)
    onAfterPrint()
  }

  applyTitle()
  window.addEventListener('beforeprint', applyTitle)
  window.addEventListener('afterprint', restoreTitle, { once: true })

  return () => {
    window.removeEventListener('beforeprint', applyTitle)
  }
}

/** Embedded mode: parent loads this document in an iframe; child signals ready then prints. */
export async function deliverEmbeddedCardExport(
  exportId: string,
  options?: CardExportDeliveryOptions,
) {
  notifyCardExportReady(exportId, options?.documentTitle)
  await new Promise((resolve) => window.setTimeout(resolve, EMBEDDED_PRINT_SETTLE_MS))

  let finished = false
  const finish = (status: CardExportStatus) => {
    if (finished) {
      return
    }
    finished = true
    notifyCardExportComplete({ exportId, status })
  }

  const unbindTitle = bindCardExportPrintTitle(options?.documentTitle, () => {
    finish('success')
  })

  applyCardExportDocumentTitle(options?.documentTitle)
  window.print()
  window.setTimeout(() => {
    unbindTitle()
    finish('success')
  }, EMBEDDED_PRINT_FALLBACK_MS)
}

/** Standalone tab/window opened with ?autoprint=1 for manual debugging. */
export async function deliverStandaloneCardExport(
  exportId: string,
  options?: CardExportDeliveryOptions,
) {
  let finished = false
  const finish = (status: CardExportStatus) => {
    if (finished) {
      return
    }
    finished = true
    notifyCardExportComplete({ exportId, status })
    if (window.opener) {
      window.setTimeout(() => window.close(), 120)
    }
  }

  window.setTimeout(() => {
    const unbindTitle = bindCardExportPrintTitle(options?.documentTitle, () => {
      finish('success')
    })

    applyCardExportDocumentTitle(options?.documentTitle)
    window.print()
    window.setTimeout(() => {
      unbindTitle()
      finish('success')
    }, STANDALONE_PRINT_FALLBACK_MS)
  }, STANDALONE_PRINT_SETTLE_MS)
}

export function failCardExport(exportId: string) {
  notifyCardExportComplete({ exportId, status: 'error' })
  if (window.opener) {
    window.setTimeout(() => window.close(), 120)
  }
}
